<template>
  <div class="dropdown-wrapper" ref="dropdownWrapper">
    <div
      class="menu-dropdown-container"
      ref="menuContainer"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
      @click.stop="toggleDropdown"
      :style="{
                backgroundColor: isHovered || isOpen ? hoverBackground : normalBackground,
            }"
    >
      <div class="image-wrapper">
        <component :is="asset" :class="assetClass" />
      </div>
      <div class="text-wrapper" ref="menuText">
        {{ text }}
      </div>
    </div>

    <transition name="dropdown">
      <div v-if="isOpen" class="ru-dropdown-menu" :style="{ width: dropdownWidth + 'px' }">
        <div
          v-for="(item, index) in items"
          :key="index"
          class="dropdown-item-container"
          ref="dropdownItems"
          @click.stop="handleItemClick(item)"
        >
          <div class="dropdown-item">
            <div class="item-image-wrapper">
              <component :is="item.asset" :class="item.assetClass" />
            </div>
            <div class="item-text-wrapper">
              {{ item.text }}
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import {
  ddcCloseAllDropdown,
  ddcRemoveDropdownCloseFunc,
  ddcSetDropdownCloseFunc,
} from '@/components/app-controller-dropdown.js';

const props = defineProps({
  asset: {
    type: [Object, String],
    required: true,
  },
  assetClass: {
    type: String,
    default: '',
  },
  text: {
    type: String,
    default: '',
  },
  items: {
    type: Array,
    default: () => [],
  },
  normalBackground: {
    type: String,
    default: 'transparent',
  },
  hoverBackground: {
    type: String,
    default: '#f0f0f0',
  },
  name: {
    type: String,
  },
});

const emit = defineEmits(['item-click', 'dropdown-opening']);

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  ddcSetDropdownCloseFunc(closeDropdown);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  ddcRemoveDropdownCloseFunc(closeDropdown);
});

const isHovered = ref(false);
const isOpen = ref(false);
const dropdownWrapper = ref(null);
const menuContainer = ref(null);
const menuText = ref(null);
const dropdownItems = ref([]);
const dropdownWidth = ref(0);

const calculateMaxWidth = async () => {
  await nextTick(); // Ждем обновления DOM

  let maxWidth = 0;

  // 1. Измеряем ширину основного элемента меню (текст + иконка + отступы)
  if (menuContainer.value) {
    const menuContainerWidth = menuContainer.value.getBoundingClientRect().width;
    maxWidth = Math.max(maxWidth, menuContainerWidth);
  }

  // 2. Временно снимаем ограничение ширины и измеряем содержимое dropdown
  if (dropdownItems.value.length > 0) {
    // Сохраняем текущие стили
    const originalStyles = dropdownItems.value.map(item => item.style.width);

    // Устанавливаем auto ширину для точного измерения
    dropdownItems.value.forEach(item => {
      item.style.width = 'auto';
      item.style.position = 'absolute';
      item.style.visibility = 'hidden';
      item.style.display = 'block';
    });

    // Измеряем ширину каждого элемента
    dropdownItems.value.forEach(item => {
      const itemWidth = item.getBoundingClientRect().width;
      maxWidth = Math.max(maxWidth, itemWidth);
    });

    // Восстанавливаем оригинальные стили
    dropdownItems.value.forEach((item, index) => {
      item.style.width = originalStyles[index];
      item.style.position = '';
      item.style.visibility = '';
      item.style.display = '';
    });
  }

  return maxWidth;
};
const updateDropdownWidth = async () => {
  dropdownWidth.value = await calculateMaxWidth();
};

const closeDropdown = () => {
  if (isOpen.value === true) {
    isOpen.value = false;
  }
};

const toggleDropdown = async () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value === true) {
    emit('dropdown-opening', props.name);
    await updateDropdownWidth();
    ddcCloseAllDropdown(closeDropdown);
  }
};

const handleItemClick = item => {
  emit('item-click', item);
  isOpen.value = false;
};

const handleClickOutside = event => {
  if (isOpen.value && dropdownWrapper.value && !dropdownWrapper.value.contains(event.target)) {
    isOpen.value = false;
  }
};

// Обновляем ширину при изменении содержимого
watch(
  () => [props.text, props.items],
  async () => {
    if (isOpen.value) {
      await updateDropdownWidth();
    }
  },
  { deep: true }
);

defineExpose({
  closeDropdown,
});
</script>

<style scoped>
.dropdown-wrapper {
  position: relative;
  display: inline-block;
}

.menu-dropdown-container {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.image-wrapper,
.item-image-wrapper {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.text-wrapper,
.item-text-wrapper {
  flex-grow: 1;
  white-space: nowrap;
}

.ru-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  margin-top: 4px;
  overflow: hidden;
  min-width: 100%;
}
.dropdown-item-container {
  display: flex;
  border-bottom: lightgrey solid 1px;
  width: 100%;
}
.dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  cursor: pointer;
  width: max-content;
  border-bottom: none;
}
.dropdown-item:hover {
  background-color: #f5f5f5;
}
.dropdown-item-container:hover {
  background-color: #f5f5f5;
}

/* Анимации для dropdown */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.3s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
